import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2, Sparkles } from "lucide-react";
import { CloneRecorder } from "../voiceClone/CloneRecorder";
import { SoundCheck } from "../voiceClone/SoundCheck";
import { REDO_MIN_SECONDS } from "../voiceClone/cloneScript";
import { API, auth, TOK, SERIF, SANS, PrimaryBtn, QuietLink, Title, startCloneCheckout } from "./shared";

const Option = ({ active, title, sub, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="w-full text-left rounded-2xl p-4 transition-colors duration-200"
    style={{ border: `1px solid ${active ? TOK.gold : TOK.goldMist}`, background: active ? "rgba(215,187,122,0.08)" : "transparent" }}>
    <div className="flex items-center justify-between">
      <span className="text-lg" style={{ color: TOK.cream, fontFamily: SERIF }}>{title}</span>
      {active && <Check size={16} style={{ color: TOK.gold }} />}
    </div>
    {sub && <div className="text-xs mt-1" style={{ color: TOK.creamMist, fontFamily: SANS }}>{sub}</div>}
  </button>
);

const OtherVoices = ({ voice, setVoice }) => (
  <div className="space-y-2" data-testid="simple-voice-others">
    <Option active={voice === "male"} title="Soulumo voice · warm male" onClick={() => setVoice("male")} testId="simple-voice-male" />
  </div>
);

// The default route — clone once, then every installation speaks in your voice.
const MainCard = ({ title, sub, active, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="w-full rounded-3xl p-6 text-left transition-transform duration-200 active:scale-[0.99]"
    style={{ background: "linear-gradient(135deg, rgba(215,187,122,0.18), rgba(215,187,122,0.04))", border: `1px solid ${TOK.gold}` }}>
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: TOK.gold, fontFamily: SANS }}><Sparkles size={12} /> Recommended</span>
      {active && <Check size={18} style={{ color: TOK.gold }} />}
    </div>
    <div className="mt-2 text-2xl font-light" style={{ color: TOK.cream, fontFamily: SERIF }}>{title}</div>
    <div className="mt-1 text-sm" style={{ color: TOK.creamMist, fontFamily: SANS }}>{sub}</div>
  </button>
);

// Polls the clone until ElevenLabs finishes (usually 10–30 s).
const useClone = () => {
  const [clone, setClone] = useState(null);
  const load = React.useCallback(() => axios.get(`${API}/api/voice-clone/me`, auth()).then((r) => setClone(r.data)).catch(() => setClone({ status: "none" })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (clone?.status !== "processing") return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [clone, load]);
  return [clone, load];
};

export const VoiceStep = ({ voice, setVoice, onContinue, onRecordMyself }) => {
  const [clone, reload] = useClone();
  const [recording, setRecording] = useState(false);
  const [redo, setRedo] = useState(false);
  const [others, setOthers] = useState(voice === "male");
  const [left, setLeft] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/voice-clone/allowance`, auth()).then((r) => setLeft(r.data)).catch(() => {});
  }, []);
  if (!clone) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" style={{ color: TOK.gold }} /></div>;
  const usable = clone.enabled || clone.trial_available;
  const ready = clone.status === "ready" && usable;
  const leftText = left && !left.exempt && clone.enabled
    ? `${left.remaining} of ${left.limit} left this month${left.credits ? ` · ${left.credits} credits` : ""}` : "Your voice is ready";
  const cloneTap = () => {
    setVoice("mine");
    if (!usable) return startCloneCheckout();
    setRecording(true);
  };
  return (
    <div className="space-y-6" data-testid="simple-voice-step">
      <Title>Choose the voice that will speak your words</Title>
      {recording && (redo || clone.status !== "ready") ? (
        <div data-testid="simple-voice-recorder"><CloneRecorder minSeconds={redo ? REDO_MIN_SECONDS : undefined} onUploaded={() => { setRecording(false); setRedo(false); reload(); }} /></div>
      ) : ready ? (
        <div className="space-y-3">
          <MainCard active={voice === "mine"} title="Use my voice" sub={leftText} onClick={() => setVoice("mine")} testId="simple-voice-mine" />
          <SoundCheck voiceId={clone.voice_id || clone.ready_at} onRedo={() => { setRedo(true); setRecording(true); }} />
          {clone.enabled && !clone.premium && <a href="/voice-clone" className="block text-xs" style={{ color: TOK.creamMist }} data-testid="simple-voice-premium-hint">Want it even closer to you? ✦ My Voice Premium</a>}
        </div>
      ) : clone.status === "processing" ? (
        <p className="text-lg italic flex items-center gap-3" style={{ color: TOK.creamDim, fontFamily: SERIF }} data-testid="simple-voice-processing">
          <Loader2 size={16} className="animate-spin" /> Learning your voice…
        </p>
      ) : clone.status === "failed" && !recording ? (
        <div className="space-y-3" data-testid="simple-voice-failed">
          <p className="text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>
            We couldn't learn your voice just now. We've been told and are fixing it — your recording is safe to redo later.
          </p>
          <PrimaryBtn testId="simple-voice-retry" onClick={() => { setRedo(true); cloneTap(); }}>Try recording again</PrimaryBtn>
        </div>
      ) : (
        <MainCard title="Clone my voice" onClick={cloneTap} testId="simple-voice-clone-btn"
          sub={`Read one short passage — then every installation speaks in your voice.${clone.trial_available ? " Your first one is on us." : ""}`} />
      )}
      {!recording && (
        <div className="space-y-2">
          <Option active={false} title="I want to record my own" sub="Read each line of your words yourself." onClick={onRecordMyself} testId="simple-voice-record-myself" />
          <Option active={voice === "female"} title="Soulumo voice" sub="Let Soulumo speak the words for me." onClick={() => setVoice("female")} testId="simple-voice-female" />
        </div>
      )}
      {(ready || voice !== "mine") && (
        <PrimaryBtn testId="simple-voice-continue" onClick={onContinue}>Continue</PrimaryBtn>
      )}
      {others ? <OtherVoices voice={voice} setVoice={setVoice} /> : (
        <QuietLink testId="simple-voice-other-toggle" onClick={() => setOthers(true)}>More voice options</QuietLink>
      )}
    </div>
  );
};
